import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { useGetAllCheckoutAsUsersQuery } from '#services/user';
import { ORDER_TYPES } from '#common/constants';

interface CheckoutAsUser {
  id?: string | number;
  username?: string;
  code?: string;
  isAdmin?: boolean;
  isStaff?: boolean;
  isSalesperson?: boolean;
  isCustomer?: boolean;
  disableShippingInfo?: boolean;
  address?: string;
  shippingAddress?: string;
  name?: string;
  shippingName?: string;
  collectionRestrictionIds?: Array<string | number>;
  [key: string]: unknown;
}

interface ComboboxOption {
  label: string;
  data: CheckoutAsUser;
}

interface CheckoutItem {
  product: {
    name?: string;
    collectionIds: Array<string | number>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface CheckoutCollection {
  id?: string | number;
  availableForAll?: boolean;
  [key: string]: unknown;
}

interface CheckoutAsProps {
  hideCustomer?: boolean | undefined;
  hideAdmin?: boolean | undefined;
  error?: boolean | undefined;
  value?: string | number | undefined;
  onChange: (event: unknown, customer: CheckoutAsUser | null) => void;
}

function CheckoutAs(props: CheckoutAsProps) {
  const { hideCustomer, hideAdmin } = props;
  const { t } = useTranslation('cart');
  let { data: allCheckoutUsers } = useGetAllCheckoutAsUsersQuery(undefined) as {
    data?: CheckoutAsUser[];
  };

  const comboboxOptions: ComboboxOption[] = [];
  if (allCheckoutUsers) {
    if (hideCustomer) {
      allCheckoutUsers = allCheckoutUsers.filter((u) => !u.isCustomer);
    }
    if (hideAdmin) {
      allCheckoutUsers = allCheckoutUsers.filter((u) => !u.isAdmin);
    }

    allCheckoutUsers.forEach((ac) => {
      let label = '';
      if (ac.isAdmin) {
        label = t('checkoutOption.admin', { username: ac.username, code: ac.code });
      } else if (ac.isStaff) {
        label = t('checkoutOption.staff', { username: ac.username, code: ac.code });
      } else if (ac.isSalesperson) {
        label = t('checkoutOption.salesperson', { username: ac.username, code: ac.code });
      } else if (ac.isCustomer) {
        label = t('checkoutOption.customer', { username: ac.username, code: ac.code });
      }

      comboboxOptions.push({
        label,
        data: ac,
      });
    });
  }

  return (
    <FormControl
      size="small"
      fullWidth
      error={props.error}
    >
      <Autocomplete
        disablePortal
        id="combo-box-demo"
        options={comboboxOptions}
        fullWidth
        size="small"
        renderInput={(params) => <TextField {...params} label={t('checkoutAs')} />}
        onChange={(event, value, _reason) => {
          const customer = value ? value.data : null;
          props.onChange(event, customer);
        }}
      />

    </FormControl>
  );
}

function CustomerInfoPanel(props: { customer: CheckoutAsUser | null }) {
  const { customer } = props;
  const { t } = useTranslation('cart');

  if (!customer) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        // mr: 30,
        p: 2,
      }}
    >
      <Grid container>
        <Grid size={4}>
          <Typography variant="body2">
            {t('panel.shippingAddress')}
          </Typography>
        </Grid>
        <Grid size={8}>
          <Typography variant="body2">
            {
              customer
              && (customer.disableShippingInfo
                ? customer.address
                : customer.shippingAddress)
            }
          </Typography>
        </Grid>

        <Grid size={4}>
          <Typography variant="body2">
            {t('panel.paymentTerms')}
          </Typography>
        </Grid>
        <Grid size={8}>
          <Typography variant="body2">
            {t('panel.reservedPayment')}
          </Typography>
        </Grid>

        <Grid size={4}>
          <Typography variant="body2">
            {t('panel.soldTo')}
          </Typography>
        </Grid>
        <Grid size={8}>
          <Typography variant="body2">
            {
              customer
              && (customer.disableShippingInfo
                ? customer.name
                : customer.shippingName)
            }
          </Typography>
        </Grid>

      </Grid>
    </Paper>
  );
}

interface CheckoutUserProps {
  error?: boolean;
  value?: string | number;
  onChange: (event: unknown, customer: CheckoutAsUser | null) => void;
  onSubmit: (orderType: number) => void;
  hideCustomer?: boolean;
  hideAdmin?: boolean;
  items: CheckoutItem[];
  collections: CheckoutCollection[];
  disabled?: boolean;
}

export default function CheckoutUser(props: CheckoutUserProps) {
  const {
    error,
    value,
    onChange,
    onSubmit,
    hideCustomer,
    hideAdmin,
    items,
    collections,
    disabled,
  } = props;

  const userInfo = useSelector(
    (state: { auth: { userInfo: CheckoutAsUser } }) => state.auth.userInfo,
  );
  const { t } = useTranslation('cart');

  const initialCustomer = userInfo.isCustomer ? userInfo : null;
  const [user, setUser] = useState<CheckoutAsUser | null>(initialCustomer);

  let orderType = ORDER_TYPES.CUSTOMER_ORDER;
  if (!userInfo.isCustomer) {
    orderType = ORDER_TYPES.ADMIN_ORDER;
  }
  if (user && user.isAdmin) {
    orderType = ORDER_TYPES.INVENTORY_ORDER;
  }

  let orderButtenLabel = '';
  if (orderType === ORDER_TYPES.CUSTOMER_ORDER) {
    orderButtenLabel = t('button.placeOrder');
  }
  if (orderType === ORDER_TYPES.ADMIN_ORDER) {
    orderButtenLabel = t('button.placeOrderAsCustomer');
  }
  if (orderType === ORDER_TYPES.INVENTORY_ORDER) {
    orderButtenLabel = t('button.placeInventoryOrder');
  }

  const collectionRestrictionProductNames: string[] = [];
  if (user && user.isCustomer) {
    // is all collection from the cart are in the current user collectionRestrictions list
    items.forEach((itm) => {
      const allowed = itm.product.collectionIds.some((cid) => {
        const col = collections.find((c) => c.id === cid);
        if (col!.availableForAll) {
          return true;
        }
        return user.collectionRestrictionIds!.includes(cid);
      });

      if (!allowed) {
        collectionRestrictionProductNames.push(itm.product.name!);
      }
    });
  }

  return (
    <>
      {
        (orderType !== ORDER_TYPES.CUSTOMER_ORDER)
        && (
          <Box sx={{ pt: 3 }}>
            <CheckoutAs
              value={value}
              error={error}
              hideCustomer={hideCustomer}
              hideAdmin={hideAdmin}
              onChange={(event, newUser) => {
                setUser(newUser);
                onChange(event, newUser);
              }}
            />
          </Box>
        )
      }

      {
        !!collectionRestrictionProductNames.length && (
          <Box sx={{ pt: 3 }}>
            <Alert severity="error">
              {t('restriction', {
                username: user!.username,
                products: collectionRestrictionProductNames.join(', '),
              })}
            </Alert>
          </Box>
        )
      }

      {
        (orderType !== ORDER_TYPES.INVENTORY_ORDER && user)
        && (
          <Box sx={{ pt: 3 }}>
            <CustomerInfoPanel customer={user} />
          </Box>
        )
      }

      <Box sx={{ py: 3 }}>
        <Button
          variant="contained"
          onClick={() => onSubmit(orderType)}
          disabled={disabled || !user || !!collectionRestrictionProductNames.length}
        >
          {orderButtenLabel}
        </Button>
      </Box>
    </>
  );
}
